import axios, { CancelToken } from 'axios'
import {API_ADMIN} from '@/utils/constants/Api'
import { IFilterList } from '@/utils/interfaces/FilterListInterface'
import { ProductModel } from '@/product/models/ProductModel'
import { ProductImageModel, ProductImageContent } from '@/product/models/ProductImageModel'
import { AttributeTypeEnum, ProductAssociableDto } from '@c2b/muven-commons'
import { ProductFormModel } from '../models/ProductFormModel'
import { ProductStoreChannelStatusModel } from '../models/ProductStoreChannelStatusModel'

interface SkuObject {
    skuCode: string;
}

interface IsMainObject {
    isMain: boolean
}

interface IsActiveObject {
    isActive: boolean
}

interface ProductListWrapped {
    page: number;
    sessionId: number;
    result: ProductModel[];
}

interface PaginationOptions {
    page: number;
    limit?: number;
    sessionId?: string;
    sort?: string;
    order?: 'asc' | 'desc';
}

export default {
    getProducts(paginationOptions: PaginationOptions, filter:IFilterList):Promise<ProductListWrapped> {
        let url = `${API_ADMIN}products?page=${paginationOptions.page}`

        if (paginationOptions.limit) {
            url += `&limit=${paginationOptions.limit}`
        }

        if (!!paginationOptions.sessionId) {
            url += `&sessionId=${paginationOptions.sessionId}`
        }

        if (paginationOptions.sort) {
            url += `&sort=${paginationOptions.sort}`
        }

        if (paginationOptions.order) {
            url += `&order=${paginationOptions.order}`
        }

        url += `${filter.toPartialQueryString()}`

        return axios.get(url)
            .then(({data}) => {
                return {
                    page: data.page,
                    sessionId: data.sessionId,
                    result: data.result.map((p:Partial<ProductModel>) => {
                      return new ProductModel(p)
                    })
                }
            })
    },

    async getProductById(id:number):Promise<ProductModel> {
        const {data} = await axios.get(`${API_ADMIN}products/${id}`);
        const product = new ProductModel(data);
        

        if (product.hasChildren()) {
            const childrenList = await this._getCompleteChildrenInfo(product)
            
            for (let i = 0; i < product.children.length; i++) {
                for (const completeChild of childrenList) {
                    if (product.children[i].id === completeChild.id) {
                        product.children[i] = completeChild
                        break
                    }
                }
            }
        }

        return product
    },

    getProductAttributeGridNames():Promise<string[]> {
        return axios.get(`${API_ADMIN}products/attributes/names?type=${AttributeTypeEnum.Grid}`)
            .then(({data}) => data);
    },

    getProductAttributeGridValues():Promise<string[]> {
        return axios.get(`${API_ADMIN}products/attributes/values?type=${AttributeTypeEnum.Grid}`)
            .then(({data}) => data);
    },

    getProductImages(id:number):Promise<ProductImageModel[]> {
        return axios.get(`${API_ADMIN}products/${id}/images`)
            .then(({data}) => {
                return data.map((p:Partial<ProductImageModel>) => new ProductImageModel(p))
            })       
    },

    getNewSkuCode(): Promise<SkuObject> {
        return axios.get(`${API_ADMIN}products/skuCode/new`)
            .then(({data}) => {
                return data;
            })
    },

    createOrUpdate(product: ProductFormModel):Promise<ProductModel> {
       
        if (product.hasId()) {
            return this.updateProduct(product)
        }

        return this.createProduct(product)
    },

    createProduct(product: ProductFormModel):Promise<ProductModel> {
        return axios.post(`${API_ADMIN}products`, product)
            .then(({data}) => {
                return new ProductModel(data);
            });
    },

    updateProduct(product: ProductFormModel):Promise<ProductModel> {
        return axios.put(`${API_ADMIN}products/${product.id}`, product)
            .then(({data}) => {
                return new ProductModel(data);
            });
    },

    createProductImages(productId: number, images:ProductImageContent[]):Promise<ProductImageModel[]> {
        return axios.post(`${API_ADMIN}products/${productId}/images`, images)
            .then(({data}) => {
                return data.map((p:Partial<ProductImageModel>) => new ProductImageModel(p))
            })       
    },

    deleteProductImages(productId:number, productImageIds: number[]):Promise<ProductImageModel[]> {
        const productIdsJoined = productImageIds.join(',')

        return axios.delete(`${API_ADMIN}products/${productId}/images?ids=${productIdsJoined}`);
    },

    updateProductImageIsMainAttribute(productId:number, idProductImage:number, obj: IsMainObject) {
        return axios.patch(`${API_ADMIN}products/${productId}/images/${idProductImage}/isMain`, obj)
            .then(({data}) => {
                return data
            });
    },

    updateProductImageIsActiveAttribute(productId:number, idProductImage:number, obj: IsActiveObject) {
        return axios.patch(`${API_ADMIN}products/${productId}/images/${idProductImage}/isActive`, obj)
            .then(({data}) => {
                return data
            });
    },

    deleteProduct(id:number):Promise<any> {
        return axios.delete(`${API_ADMIN}products/${id}`)
    },

    forceChannelsSync(id:number):Promise<void> {
        return axios.post(`${API_ADMIN}products/${id}/force_channels_sync`)
    },

    getAssociableProductListInBackend(storeId: number, channelId:number, queryParams: string):Promise<ProductAssociableDto[]> {
        return axios.get(`${API_ADMIN}products/association/in_muven/from_store/${storeId}/from_channel/${channelId}${queryParams}`)
            .then(({data}) => {
                return data.map((p: ProductAssociableDto) => new ProductAssociableDto(p))
            })
    }, 

    getAssociableProductInChannelByAnyInfo(storeId: number, channelId:number, productCode: string):Promise<ProductAssociableDto[]> {
        return axios.get(`${API_ADMIN}products/association/in_channel/${channelId}?info=${encodeURIComponent(productCode)}&idStore=${storeId}`)
            .then(({data}) => {
                return data.map((p: ProductAssociableDto) => new ProductAssociableDto(p))
            })
    }, 

    getAssociableProductInChannelByIdExternalChannel(storeId: number, channelId:number, productCode: string, config: {cancelToken?: CancelToken} = {}):Promise<ProductAssociableDto> {
        return axios.get(`${API_ADMIN}products/association/in_channel/${channelId}?idExternalChannel=${encodeURIComponent(productCode)}&idStore=${storeId}`, config)
            .then(({data}) => {
                return new ProductAssociableDto(data)
            })
    }, 

    updateProductAssociation(storeId: number, channelId:number, productAssociableList: Array<Partial<ProductAssociableDto>>):Promise<ProductAssociableDto[]> {
        return axios.post(`${API_ADMIN}products/association/from_store/${storeId}/in_channel/${channelId}`, productAssociableList)
            .then(({data}) => {
                return (data as ProductAssociableDto[]).map((d) => new ProductAssociableDto(d))
            })
    },
    getAssociableProductMultiListInBackend(storeId: number, channelId:number, queryParams: string):Promise<ProductAssociableDto[]> {
        return axios.get(`${API_ADMIN}products/association_multi/in_muven/from_store/${storeId}/from_channel/${channelId}${queryParams}`)
            .then(({data}) => {
                return data.map((p: ProductAssociableDto) => new ProductAssociableDto(p))
            })
    },
    updateProductMultiAssociation(storeId: number, channelId:number, productAssociableList: Array<Partial<ProductAssociableDto>>):Promise<ProductAssociableDto[]> {
        return axios.post(`${API_ADMIN}products/association_multi/from_store/${storeId}/in_channel/${channelId}`, productAssociableList)
            .then(({data}) => {
                return (data as ProductAssociableDto[]).map((d) => new ProductAssociableDto(d))
            })
    },
    deleteProductMultiAssociation(productStoreChannelId: number):Promise<void> {
        return axios.delete(`${API_ADMIN}products/association_multi/in_muven/product_store_channel/${productStoreChannelId}`);
    },
    updateProductStoreChannelStatus(productChannelStatus: ProductStoreChannelStatusModel[]):Promise<ProductModel> {
        return axios.patch(`${API_ADMIN}products/channels/status`, productChannelStatus)
            .then(({data}) => {
                return new ProductModel(data)
            })
    },
    deleteImageList(idProduct: number, idImageList: number[]):Promise<any> {
        return axios.delete(`${API_ADMIN}products/${idProduct}/images?ids=${idImageList.join(',')}`)
            .then(({data}) => {
                return data
            });
    },    
    async _getCompleteChildrenInfo(product: ProductModel): Promise<ProductModel[]> {
        const childIdList = product.children.map((child) => child.id)
        const childrenList:ProductModel[] = []
        const MAX_ID_LIST_ON_REQUEST = 10
        let tmpChildIds = []
        let startingPos = 0
        let count = 0
        const finalList = []
        
        do {
            while (count < MAX_ID_LIST_ON_REQUEST && !!childIdList[startingPos]) {
                tmpChildIds.push(childIdList[startingPos])
                startingPos++
                count++
            }

            finalList.push(tmpChildIds)

            count = 0
            tmpChildIds = []
        } while (!!childIdList[startingPos])

        const result = await Promise.all(
            finalList.map(async (idList) => axios.get(`${API_ADMIN}products/${product.id}/children?idList=${idList.join(',')}`))
        ) as any[]

        result.forEach((r) => {
            const dataParsed = r.data.map((data:ProductModel) => new ProductModel(data as ProductModel))
            childrenList.push(...dataParsed)
        })

        return childrenList
    },
}
